export interface FeatureDefaultValue {
    /** Identifier of the feature.*/
    id: FeatureId;

    /** Default value of the feature. Null or undefined is interpreted as false.
     * This can be set by saving a feature default value in the database.
     */
    defaultValue?: boolean;

    /** Last time this value was updated */
    lastUpdated?: Date;
}

export interface Feature extends FeatureDefaultValue {
    /** Name of the feature */
    name: string;

    /** Description of the feature */
    description: string;

    /**
     * True if this feature does nothing in the codebase
     * and is preserved for backward compatibility.
     */
    removed?: boolean;

    /**
     * True if this feature is for automated testing purposes only.
     */
    hidden?: boolean;
}

export enum FeatureId {
    /** Example feature for testing. */
    HelloWorld = 'hello-world',
    /** Enables the iHeartRadio integration on walt */
    IHeartRadioIntegration = 'iheartradio-integration',
    /** Personal Gas Monitors. */
    GasMonitors = 'gas-monitors',
    /** Report safety observations. */
    SafetyObservation = 'safety-observation',
    /** Switch walt to offline radio in an outage. */
    WaltOfflineMode = 'walt-offline-mode',
    /** Automatically switch walt to offline mode when network or mqtt is unavailable for a period of time. */
    WaltOfflineModeNetworkInitiated = 'walt-offline-mode-network-initiated',
    /** Enables walt users to trigger voice assistant */
    AiVoiceAssistant = 'ai-voice-assistant',
    /** Enables mobile PTT button to act like walt */
    MobilePttButton = 'mobile-ptt-button',
    /** Routes live PTT audio through the ptt-service micro-service instead of the monolith */
    PttIsolation = 'ptt-isolation',
    /** Only allows users to be logged into a single walt at a time */
    SingleWaltLogin = 'single-walt-login',
    /** Enables users to change sim settings for sites via console */
    ConsoleSimSettings = 'console-sim-settings',
    /**
     * Sync in native Android.
     */
    WaltNativeSync = 'walt-native-sync',
    /** Enables the new menu layout */
    NewMenu = 'walt-new-menu',
    /**
     * Enables the new channel creation flow in walt
     */
    NewChannelFlow = 'walt-new-channel-flow',
    /** Hatch Speaker Mic */
    HatchSpeakerMic = 'hatch-speaker-mic',
    /** Enables users to access the mesh deployment tool UI */
    MeshDeploymentTool = 'mesh-deployment-tool',
    /** Enables the rollout of new enhancements available for Walt 2 Device Management */
    Walt2DeviceManagementEnhancements = 'walt2-device-management-enhancements',
    /**
     * Enables the Walt Credential to lock the user out of the walt
     */
    WaltCredentialEnforced = 'walt-credential-enforced',
    /**
     * When enabled, the volume of panic alerts will defer to the current volume setting rather than maximum.
     */
    PanicVolumeControl = 'panic-volume-control',
    /**
     * Enables the translator feature in walt
     */
    WaltTranslator = 'walt-translator',
    /**
     * Enables the Android PTT Framework on the mobile app
     */
    AndroidPttFramework = 'android-ptt-framework',
    /**
     * Enables new sharing users across accounts flow for given sites
     */
    ShareUsersAcrossAccounts = 'share-users-across-accounts',
    /**
     * Enables the NWS (National Weather Service) weather alerts workflow trigger
     */
    NwsWeatherAlerts = 'nws-weather-alerts',
    /**
     * Log in to walt without internet connectivity. Requires "Native Sync" and "Walt Credential Enforced" to be enabled.
     */
    OfflineLogin = 'offline-login',
    /**
     * Enables the network scheduler to toggle airplane mode on and off when the device is in a poor network state.
     */
    EnableNetSchedulerAirplaneToggle = 'enable-net-scheduler-airplane-toggle',
    /**
     * When enabled, a walt 5G turns its mesh radios off while docked on a wrangler and back on when undocked.
     */
    DisableMeshWhenDocked = 'disable-mesh-when-docked',
    /**
     * Routes Mongo change events through RabbitMQ instead of dispatching them in-process.
     *
     * Backend-only infrastructure flag: it changes how change-stream consumers are invoked, not anything
     * a client can see. Facility-level flags are ignored — a database write is not facility-scoped.
     * Routes Mongo change events through RabbitMQ instead of dispatching in-process. Backend-only, invisible to
     * clients, and facility-level flags are ignored since a write is not facility-scoped.
     */
    RabbitMqChangeStream = 'rabbitmq-change-stream',
    /**
     * Gates the safety feature set: panic button mode, silent alerts and per-alert-type
     * volume, hard fall detection, and lone worker inactivity monitoring.
     */
    SafetyEnhancements = 'safety-enhancements',
    /**
     * Distributes walt firmware updates over the DECT mesh -- gossiping versions and flooding update
     * bytes -- instead of every device downloading its own. Off by default, because flooding occupies
     * the shared mesh channel. Scoped to DECT: the bluetooth and wifi-direct transports are
     * point-to-point and keep working either way.
     */
    MeshOta = 'mesh-ota',
    /**
     * Transcribe PTT audio through the AI cascade on the Azure Foundry transcription API.
     *
     * Covers BOTH cascade engines: MAI where it can hear the language, and Azure's own models
     * behind it for the six it cannot and as the retry when it fails. There is no separate flag for
     * the second, so this turns the whole cascade on or off.
     *
     * Off is today's legacy behavior exactly. A clip the cascade cannot serve -- a language neither
     * engine covers, an open circuit, a failure -- falls back to legacy regardless, so the flag
     * turns the cascade on rather than turning legacy off.
     */
    MicrosoftAiTranscription = 'microsoft-ai-transcription',
    /** Powers the walt LoRa radio off for the account. Off (the default) leaves LoRa on, including its relay of PTT into DECT. */
    DisableLora = 'disable-lora',
    /**
     * Serves DVR and person badge history for dropped months from the parquet archive, and lets the archive
     * drop the account's archived months from Mongo. Account-level; once months are dropped, off hides them.
     */
    BadgeHistoryArchive = 'badge-history-archive',
    /** Detect which of a bilingual user's primary and secondary languages was spoken or typed. */
    BilingualSpeech = 'bilingual-speech',
    /**
     * Runs EMQX beside Aedes in this environment: the backend serves EMQX's auth hooks, mirrors its presence and
     * publishes to EMQX subscribers too. Read from the platform default only; account and facility flags are ignored.
     */
    EmqxBridge = 'emqx-bridge',
    /**
     * Reserved for moving an account's clients onto EMQX; nothing reads it yet. Account-level only, since one account
     * split across two brokers would break PTT between its users; facility flags will be ignored.
     */
    EmqxClients = 'emqx-clients',
    /** Lets users pick the voice (Voice 1 male, Voice 2 female) others hear when their messages are read aloud. */
    MaleFemaleVoices = 'male-female-voices',
    /** Hides dialect selection and transcribes every speaker in their language's default dialect; saved dialects are kept. */
    RemoveDialects = 'remove-dialects',
    /** Lets people use the Weavix Desktop App; off shows a notice and clears its live data. */
    DesktopRadio = 'desktop-radio',
}

export const Features: { [key in FeatureId]: Feature } = {
    [FeatureId.HelloWorld]: {
        id: FeatureId.HelloWorld,
        name: 'Hello World',
        description: 'I hope you are having a great day!',
        hidden: true,
    },
    [FeatureId.IHeartRadioIntegration]: {
        id: FeatureId.IHeartRadioIntegration,
        name: 'iHeartRadio Integration',
        description: 'Enables the iHeartRadio integration on walt',
    },
    [FeatureId.GasMonitors]: {
        id: FeatureId.GasMonitors,
        name: 'Gas Monitors',
        description: 'Personal Gas Monitors.',
    },
    [FeatureId.SafetyObservation]: {
        id: FeatureId.SafetyObservation,
        name: 'Safety Observation',
        description: 'Report safety observations.',
    },
    [FeatureId.WaltOfflineMode]: {
        id: FeatureId.WaltOfflineMode,
        name: 'Offline Mode',
        description: 'Switch walt to offline radio in an outage.',
    },
    [FeatureId.WaltOfflineModeNetworkInitiated]: {
        id: FeatureId.WaltOfflineModeNetworkInitiated,
        name: 'Offline Mode Network Initiated',
        description: 'Automatically switch walt to offline mode when network or mqtt is unavailable for a period of time.',
    },
    [FeatureId.AiVoiceAssistant]: {
        id: FeatureId.AiVoiceAssistant,
        name: 'AI Voice Assistant',
        description: 'Enables walt users to trigger and use voice assistant',
    },
    [FeatureId.MobilePttButton]: {
        id: FeatureId.MobilePttButton,
        name: 'Mobile PTT Button',
        description: 'Enables mobile PTT button to act like walt',
    },
    [FeatureId.PttIsolation]: {
        id: FeatureId.PttIsolation,
        name: 'PTT Isolation',
        description: 'Routes live PTT audio through the ptt-service micro-service instead of the monolith',
        defaultValue: false,
    },
    [FeatureId.SingleWaltLogin]: {
        id: FeatureId.SingleWaltLogin,
        name: 'Single Walt Login',
        description: 'Only allows users to be logged into a single walt at a time',
    },
    [FeatureId.ConsoleSimSettings]: {
        id: FeatureId.ConsoleSimSettings,
        name: 'Console SIM Settings',
        description: 'Enables users to change sim settings for sites via console',
    },
    [FeatureId.WaltNativeSync]: {
        id: FeatureId.WaltNativeSync,
        name: 'walt Native Sync',
        description: 'Improve performance of walt by running sync in native code.',
    },
    [FeatureId.NewMenu]: {
        id: FeatureId.NewMenu,
        name: 'walt New Menu',
        description: 'Enables the new menu layout',
        defaultValue: false,
    },
    [FeatureId.NewChannelFlow]: {
        id: FeatureId.NewChannelFlow,
        name: 'walt New Channel Flow',
        description: 'Enables the new channel creation flow in walt',
        defaultValue: false,
    },
    [FeatureId.HatchSpeakerMic]: {
        id: FeatureId.HatchSpeakerMic,
        name: 'Hatch Speaker Mic',
        description: 'Enables the Hatch Speaker Mic integration',
        defaultValue: false,
    },
    [FeatureId.MeshDeploymentTool]: {
        id: FeatureId.MeshDeploymentTool,
        name: 'Mesh Deployment Tool',
        description: 'Enables users to access the mesh deployment tool UI',
        defaultValue: false,
    },
    [FeatureId.Walt2DeviceManagementEnhancements]: {
        id: FeatureId.Walt2DeviceManagementEnhancements,
        name: 'Walt 2 Device Management Enhancements',
        description: 'Enables the rollout of new enhancements available for Walt 2 Device Management',
        defaultValue: false,
    },
    [FeatureId.WaltCredentialEnforced]: {
        id: FeatureId.WaltCredentialEnforced,
        name: 'Walt Credential Enforced',
        description: 'Enforces the presence of a valid Walt Credential to use the Walt',
        defaultValue: false,
    },
    [FeatureId.PanicVolumeControl]: {
        id: FeatureId.PanicVolumeControl,
        name: 'Panic Volume Control',
        description: 'When enabled, the volume of panic alerts will defer to the current volume setting rather than maximum.',
        defaultValue: false,
    },
    [FeatureId.WaltTranslator]: {
        id: FeatureId.WaltTranslator,
        name: 'Walt Translator',
        description: 'Enables the translator feature in walt',
        defaultValue: false,
    },
    [FeatureId.AndroidPttFramework]: {
        id: FeatureId.AndroidPttFramework,
        name: 'Android PTT Framework',
        description: 'Enables the Android PTT Framework on the mobile app',
        defaultValue: false,
    },
    [FeatureId.ShareUsersAcrossAccounts]: {
        id: FeatureId.ShareUsersAcrossAccounts,
        name: 'Share Users Across Accounts',
        description: 'Enables new sharing users across accounts flow for given sites',
        defaultValue: false,
    },
    [FeatureId.NwsWeatherAlerts]: {
        id: FeatureId.NwsWeatherAlerts,
        name: 'NWS Weather Alerts',
        description: 'Enables the NWS (National Weather Service) weather alerts workflow trigger',
        defaultValue: false,
    },
    [FeatureId.OfflineLogin]: {
        id: FeatureId.OfflineLogin,
        name: 'Walt Offline Login',
        description: 'Log in to walt without internet connectivity. Requires "Native Sync" and "Walt Credential Enforced" to be enabled.',
        defaultValue: false,
    },
    [FeatureId.EnableNetSchedulerAirplaneToggle]: {
        id: FeatureId.EnableNetSchedulerAirplaneToggle,
        name: 'Enable Network Scheduler Airplane Toggle',
        description: 'Enables the network scheduler to toggle airplane mode on and off when the device is in a poor network state.',
        defaultValue: false,
    },
    [FeatureId.MeshOta]: {
        id: FeatureId.MeshOta,
        name: 'Mesh OTA',
        description: 'Distributes walt firmware updates peer-to-peer over the DECT mesh. Bluetooth and wifi-direct are unaffected.',
        defaultValue: false,
    },
    [FeatureId.DisableMeshWhenDocked]: {
        id: FeatureId.DisableMeshWhenDocked,
        name: 'Disable Mesh When Docked',
        description: 'Turns a walt 5G\'s mesh radios off while docked on a wrangler and back on when undocked',
        defaultValue: true,
    },
    [FeatureId.RabbitMqChangeStream]: {
        id: FeatureId.RabbitMqChangeStream,
        name: 'RabbitMQ Change Stream',
        description: 'Publishes Mongo change events to RabbitMQ for asynchronous, fleet-wide processing. '
            + 'When off, change-stream consumers are invoked in-process by the server that made the write. '
            + 'Set the default value to control the global rollout and use account flags to opt individual '
            + 'accounts in or out. Facility-level flags have no effect.',
        defaultValue: false,
    },
    [FeatureId.MicrosoftAiTranscription]: {
        id: FeatureId.MicrosoftAiTranscription,
        name: 'Microsoft AI Transcription',
        description: 'Transcribes PTT audio through the AI cascade instead of the legacy Azure '
            + 'Speech recognizer. Microsoft\'s MAI model serves most languages; the six it cannot '
            + 'hear — Burmese, Albanian, Pashto, Lao, Amharic and Khmer — are served by Azure\'s own '
            + 'models on the same API, which also retries a clip MAI fails. Anything the cascade '
            + 'cannot serve still falls back to legacy. Off is the legacy Azure Speech behavior.',
        defaultValue: false,
    },
    [FeatureId.BadgeHistoryArchive]: {
        id: FeatureId.BadgeHistoryArchive,
        name: 'Badge History Archive',
        description: 'Serves DVR playback and person location history for months older than the '
            + 'current and previous month from the badgeUpdates parquet archive once they are dropped '
            + 'from Mongo, and reads every month of a range rather than only its first and last. '
            + 'Off reads Mongo only, exactly as before. Facility-level flags have no effect. '
            + 'Turning it on also lets the archive drop that account\'s archived months from Mongo; '
            + 'once any are dropped, turning it off hides them from DVR and history.',
        defaultValue: false,
    },
    [FeatureId.SafetyEnhancements]: {
        id: FeatureId.SafetyEnhancements,
        name: 'Safety Enhancements',
        description: 'Enables the safety feature set: panic button mode, silent alerts and per-alert-type volume, hard fall detection, and lone worker inactivity monitoring.',
        defaultValue: false,
    },
    [FeatureId.DisableLora]: {
        id: FeatureId.DisableLora,
        name: 'Disable LoRa',
        description: 'Powers the walt LoRa radio off, so walts mesh over DECT only and no longer relay PTT from walts beyond DECT range.',
        defaultValue: false,
    },
    [FeatureId.BilingualSpeech]: {
        id: FeatureId.BilingualSpeech,
        name: 'Bilingual Speech',
        description: 'Lets users set a secondary language, and detects which of their two languages '
            + 'was used when they talk on PTT or type a message. Off ignores the secondary language.',
        defaultValue: false,
    },
    [FeatureId.EmqxBridge]: {
        id: FeatureId.EmqxBridge,
        name: 'EMQX Bridge',
        description: 'Runs EMQX beside the current MQTT broker in this environment, so accounts can later move to EMQX '
            + 'one at a time. Set the platform default; account and facility settings have no effect.',
        defaultValue: false,
    },
    [FeatureId.EmqxClients]: {
        id: FeatureId.EmqxClients,
        name: 'EMQX Clients',
        description: 'Reserved for moving this account onto EMQX; it has no effect yet. Account-level only; '
            + 'facility settings will have no effect.',
        defaultValue: false,
    },
    [FeatureId.MaleFemaleVoices]: {
        id: FeatureId.MaleFemaleVoices,
        name: 'Male/Female Voices',
        description: 'Lets users choose Voice 1 (male) or Voice 2 (female) for the text-to-speech others hear '
            + 'when their text or translated messages are read aloud. Off uses Voice 1 for everyone.',
        defaultValue: false,
    },
    [FeatureId.RemoveDialects]: {
        id: FeatureId.RemoveDialects,
        name: 'Remove Dialects',
        description: 'Hides dialect selection and transcribes everyone in their language\'s default dialect '
            + '(e.g. Spanish as es-MX). Saved dialects are kept, so turning this off restores them.',
        defaultValue: false,
    },
    [FeatureId.DesktopRadio]: {
        id: FeatureId.DesktopRadio,
        name: 'Desktop Radio',
        description: 'Lets people use the Weavix Desktop App. Off shows a notice after sign-in and clears its live data.',
        defaultValue: false,
    },
};
